// src/pages/Teto_vs_egen/Info.jsx
import { useNavigate } from 'react-router-dom';
import './Info/Info.css';
import { lazy, Suspense, useEffect, useState, useTransition, useRef } from 'react';
import { apiFetch } from '../../api/client';
import { getToken, isLoggedIn } from '../../auth/auth';
import { useTranslation } from 'react-i18next';
import ConsentModal from './ConsentModal/ConsentModal';
import { requireLoginOrContinue } from '../../auth/returnTo';
import { writeStoredFaceResult } from '../../utils/faceResultStorage';
import {
  getAnalysisFailureReason,
  getFaceAnalysisErrorType,
  getDurationBucket,
  getFileSizeBucket,
  getFileType,
  getPhotoFailureReason,
  trackEvent,
} from '../../utils/analytics';
import { getLocalizedError } from '../../api/errors';
import { prepareImageUploadAsset } from '../../utils/imageUpload';

const RealtimeFaceRadar = lazy(() => import('./Info/RealtimeFaceRadar'));

function useForm() {
  const [gender, setGender] = useState('');
  const [image, setImage] = useState(null);
  const [imageOrigin, setImageOrigin] = useState('upload');
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();
  const [isPending, startTransition] = useTransition();
  const attemptCountRef = useRef(0);

  const handleSubmit = (consent) => {
    if (!gender || !image) {
      alert(t('face_info.alert.select_all'));
      return;
    }

    startTransition(async () => {
      const token = getToken();
      const attemptNumber = attemptCountRef.current + 1;
      attemptCountRef.current = attemptNumber;
      const startedAt = performance.now();

      const formData = new FormData();
      formData.append('gender', gender);
      formData.append('file', image);
      formData.append('consent', consent);
      formData.append('locale', i18n.resolvedLanguage || i18n.language);

      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      try {
        trackEvent('analysis_started', {
          analysis_type: 'face',
          attempt_number: attemptNumber,
          is_logged_in: Boolean(token),
          credit_required: false,
        });

        const data = await apiFetch('/analyze', {
          method: 'POST',
          headers,
          body: formData,
        });

        const hasUsableResult = Boolean(
          data?.result_id &&
          data?.labels &&
          (data?.persona || data?.masculinity_label)
        );
        if (hasUsableResult) {
          trackEvent('analysis_completed', {
            analysis_type: 'face',
            attempt_number: attemptNumber,
            duration_bucket: getDurationBucket(performance.now() - startedAt),
            result_type: data.persona?.code,
          });
          trackEvent('face_analysis_success', {
            feature_type: 'face', analysis_mode: 'standard',
          });
        } else {
          trackEvent('analysis_failed', {
            analysis_type: 'face',
            attempt_number: attemptNumber,
            failure_reason: 'invalid_response',
          });
          trackEvent('face_analysis_failed', {
            feature_type: 'face', failure_stage: 'response', error_type: 'invalid_response',
          });
        }

        if (data?.save_status?.saved) {
          console.info('[FaceAnalysis] Result saved:', data.save_status);
          window.dispatchEvent(new CustomEvent('results-updated', {
            detail: { endpoint: '/analyze', mode: 'server', saveStatus: data.save_status },
          }));
        } else {
          console.error('[FaceAnalysis] Result save failed or skipped:', data?.save_status);
        }

        if (import.meta.env.DEV) {
          console.info('[FaceAnalysis] mesh_overlay_data_url exists', Boolean(data?.mesh_overlay_data_url));
          console.info('[FaceAnalysis] input_image_data_url exists', Boolean(data?.input_image_data_url));
        }

        const fallbackPreviewUrl = URL.createObjectURL(image);
        const imagePreviewUrl = data?.input_image_data_url || fallbackPreviewUrl;
        const resultState = {
          result: data,
          imagePreviewUrl,
          input_image_data_url: data?.input_image_data_url || '',
          mesh_overlay_data_url: data?.mesh_overlay_data_url || data?.mesh_image || '',
        };

        if (!isLoggedIn()) {
          const storedResultState = imageOrigin === 'realtime_radar'
            ? {
                ...resultState,
                result: Object.fromEntries(Object.entries(resultState.result || {}).filter(([key]) => ![
                  'input_image_data_url', 'mesh_overlay_data_url', 'mesh_image',
                ].includes(key))),
                imagePreviewUrl: '',
                input_image_data_url: '',
                mesh_overlay_data_url: '',
              }
            : resultState;
          writeStoredFaceResult(storedResultState);
          requireLoginOrContinue({
            isAuthenticated: false,
            location: {
              pathname: '/analyze',
              search: '',
              hash: '',
            },
            navigate,
            fallback: '/analyze',
          });
          return;
        }

        navigate('/analyze', {
          state: {
            ...resultState,
          },
        });
      } catch (err) {
        const httpStatus = Number(err?.status || 0) || undefined;
        trackEvent('analysis_failed', {
          analysis_type: 'face',
          attempt_number: attemptNumber,
          failure_reason: getAnalysisFailureReason(err),
          http_status: httpStatus,
        });
        trackEvent('face_analysis_failed', {
          feature_type: 'face',
          failure_stage: httpStatus ? 'response' : 'request',
          error_type: getFaceAnalysisErrorType(err),
        });
        if (['face_not_detected', 'unsupported_image_format', 'upload_too_large'].includes(err?.code)) {
          trackEvent('photo_validation_failed', {
            analysis_type: 'face',
            photo_slot: 'single',
            failure_reason: getPhotoFailureReason(err),
          });
        }
        console.error('전송 실패:', err);
        if (err.message.includes('No access token found')) {
          alert(t('face_info.alert.login_required'));
          navigate('/login');
        } else {
          alert(getLocalizedError(err, i18n.resolvedLanguage || i18n.language));
        }
      }
    });
  };

  return { gender, setGender, image, setImage, setImageOrigin, handleSubmit, isPending };
}

function Info() {
  const { t, i18n } = useTranslation();
  const { gender, setGender, image, setImage, setImageOrigin, handleSubmit, isPending } = useForm();
  const [showConsent, setShowConsent] = useState(false);
  const [showFaceRadar, setShowFaceRadar] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [isPreparingImage, setIsPreparingImage] = useState(false);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const pageViewTrackedRef = useRef(false);
  const formStartTrackedRef = useRef(false);
  const isBusy = isPending || isPreparingImage;

  const trackFormStart = () => {
    if (formStartTrackedRef.current) return;
    formStartTrackedRef.current = true;
    trackEvent('form_start', { feature_type: 'face', source_page: 'face_info' });
  };

  useEffect(() => {
    if (pageViewTrackedRef.current) return;
    pageViewTrackedRef.current = true;
    trackEvent('analysis_page_viewed', {
      analysis_type: 'face',
      entry_source: 'direct_or_navigation',
    });
  }, []);

  useEffect(() => {
    const adUnit = 'DAN-Oxu2hp70USJgW3wn';
    const adWidth = 250;
    const adHeight = 250;

    try {
      if (typeof window === 'undefined') return;

      const src = 'https://t1.daumcdn.net/kas/static/ba.min.js';
      if (!document.querySelector(`script[src="${src}"]`)) {
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        document.head.appendChild(s);
      }

      if (typeof window.kakaoAdFit === 'undefined') {
        window.kakaoAdFit = [];
      }

      if (Array.isArray(window.kakaoAdFit)) {
        window.kakaoAdFit.push({
          display: 'adfit',
          adUnit,
          adWidth,
          adHeight,
        });
      } else if (typeof window.kakaoAdFit.push === 'function') {
        window.kakaoAdFit.push({
          display: 'adfit',
          adUnit,
          adWidth,
          adHeight,
        });
      }
    } catch (e) {
      console.error('[AdFit] init error:', e);
    }
  }, []);

  const handleAnalyzeClick = () => setShowConsent(true);

  const handleAgree = () => {
    setShowConsent(false);
    handleSubmit(true);
  };

  const handleDisagree = () => {
    setShowConsent(false);
    handleSubmit(false);
  };

  const handleUploadClick = (inputRef) => {
    trackFormStart();
    trackEvent('photo_select_started', {
      analysis_type: 'face',
      photo_slot: 'single',
    });
    trackEvent('face_upload_start', {
      feature_type: 'face',
      image_source: inputRef === cameraInputRef ? 'camera' : 'upload',
      source_page: 'face_info',
    });
    inputRef.current?.click();
  };

  const handleImageSelected = async (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    setIsPreparingImage(true);
    setImage(null);
    setImagePreviewUrl('');

    try {
      const asset = await prepareImageUploadAsset({
        file,
        filenameBase: 'face-upload',
        slot: 'face',
        source: event.target === cameraInputRef.current ? 'camera' : 'gallery',
      });
      setImage(asset.file);
      setImageOrigin('upload');
      setImagePreviewUrl(asset.previewUrl);
      trackEvent('photo_selected', {
        analysis_type: 'face',
        photo_slot: 'single',
        file_type: getFileType(asset.sourceType),
        file_size_bucket: getFileSizeBucket(file.size),
      });
      trackEvent('face_upload_success', {
        feature_type: 'face',
        image_source: event.target === cameraInputRef.current ? 'camera' : 'upload',
        file_type: getFileType(asset.sourceType),
        source_page: 'face_info',
      });
    } catch (error) {
      trackEvent('photo_validation_failed', {
        analysis_type: 'face',
        photo_slot: 'single',
        failure_reason: getPhotoFailureReason(error),
      });
      trackEvent('face_analysis_failed', {
        feature_type: 'face', failure_stage: 'validation',
        error_type: getFaceAnalysisErrorType(error), source_page: 'face_info',
      });
      console.error('이미지 준비 실패:', error);
      alert(getLocalizedError(error, i18n.resolvedLanguage || i18n.language));
    } finally {
      setIsPreparingImage(false);
      event.target.value = '';
    }
  };

  const handleRadarCapture = async (file) => {
    setIsPreparingImage(true);
    try {
      const asset = await prepareImageUploadAsset({
        file,
        filenameBase: 'face-radar-capture',
        slot: 'face',
        source: 'camera',
      });
      setImage(asset.file);
      setImageOrigin('realtime_radar');
      setImagePreviewUrl(asset.previewUrl);
      setShowFaceRadar(false);
      trackFormStart();
      trackEvent('photo_selected', {
        analysis_type: 'face', photo_slot: 'single',
        file_type: getFileType(asset.sourceType), file_size_bucket: getFileSizeBucket(file.size),
      });
      trackEvent('face_upload_success', {
        feature_type: 'face', image_source: 'realtime_camera',
        file_type: getFileType(asset.sourceType), source_page: 'face_info',
      });
    } catch (error) {
      trackEvent('photo_validation_failed', {
        analysis_type: 'face', photo_slot: 'single', failure_reason: getPhotoFailureReason(error),
      });
      console.error('실시간 카메라 이미지 준비 실패:', error);
      alert(getLocalizedError(error, i18n.resolvedLanguage || i18n.language));
    } finally {
      setIsPreparingImage(false);
    }
  };

  return (
    <div className="bg-beige" style={{ minHeight: '100vh', width: '100vw' }}>
      <div className="info-container">
        <h2>{t('face_info.start_title')}</h2>

        <div className="notice-card">
          <div className="notice-header">
            <span className="warning-icon">⚠️</span>
            <span className="notice-title">{t('face_info.notice_title')}</span>
          </div>
          <ul className="notice-list">
            {t('face_info.guide_items', { returnObjects: true }).map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="form-section">
          <label className="label-title">{t('face_info.select_gender')}</label>
          <div className="radio-group">
            <label className={`radio-button ${gender === 'male' ? 'active' : ''}`}>
              <input
                type="radio"
                name="gender"
                value="male"
                checked={gender === 'male'}
                onChange={() => { trackFormStart(); setGender('male'); }}
              />
              {t('face_info.male')}
            </label>
            <label className={`radio-button ${gender === 'female' ? 'active' : ''}`}>
              <input
                type="radio"
                name="gender"
                value="female"
                checked={gender === 'female'}
                onChange={() => { trackFormStart(); setGender('female'); }}
              />
              {t('face_info.female')}
            </label>
          </div>

          <label className="label-title" style={{ marginTop: '1rem' }}>
            {t('face_info.upload_desc')}
          </label>

          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageSelected}
            disabled={isBusy}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="user"
            style={{ display: 'none' }}
            onChange={handleImageSelected}
            disabled={isBusy}
          />

          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              marginTop: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <button
              type="button"
              onClick={() => handleUploadClick(galleryInputRef)}
              disabled={isBusy}
              style={{
                flex: '1 1 140px',
                minHeight: '48px',
                padding: '0.75rem 1rem',
                background: 'rgba(255,255,255,0.07)',
                border: '1.5px solid rgba(255,255,255,0.18)',
                borderRadius: '12px',
                color: '#e2e8f0',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: isBusy ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'background 0.2s',
              }}
            >
              🖼️ {t('face_info.select_from_gallery')}
            </button>
            <button
              type="button"
              onClick={() => handleUploadClick(cameraInputRef)}
              disabled={isBusy}
              style={{
                flex: '1 1 140px',
                minHeight: '48px',
                padding: '0.75rem 1rem',
                background: 'linear-gradient(135deg, rgba(96,165,250,0.15), rgba(168,85,247,0.15))',
                border: '1.5px solid rgba(96,165,250,0.4)',
                borderRadius: '12px',
                color: '#93c5fd',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: isBusy ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'background 0.2s',
              }}
            >
              📷 {t('face_info.take_with_camera')}
            </button>
            <button
              type="button"
              className="face-radar-entry"
              onClick={() => { trackFormStart(); setShowFaceRadar(true); }}
              disabled={isBusy}
            >
              ◎ {t('face_info.radar.open')}
            </button>
          </div>

          <div className="preview-container" style={{ marginTop: '1rem' }}>
            {image && imagePreviewUrl ? (
              <img
                src={imagePreviewUrl}
                alt={t('face_info.preview_alt')}
                className="image-preview"
              />
            ) : (
              <div className="image-placeholder" />
            )}
          </div>

          <button className="submit-button" onClick={handleAnalyzeClick} disabled={isBusy}>
            {isPending ? t('face_info.analyzing', '분석 중...') : t('face_info.analyze_request')}
          </button>

          {isPending && (
            <div style={{ marginTop: '1.5rem', textAlign: 'center', color: '#60a5fa' }}>
              <div className="spinner"></div>
              <p style={{ marginTop: '0.8rem', fontWeight: 'bold' }}>AI가 얼굴 특징을 정밀 분석하고 있습니다...</p>
              <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>최대 10초 정도 소요될 수 있습니다.</p>
            </div>
          )}

          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <ins
              className="kakao_ad_area"
              style={{
                display: 'none',
                width: 250,
                height: 250,
                margin: '0 auto',
                outline: '1px dashed #aaa',
                background: 'rgba(0,0,0,.05)',
              }}
              data-ad-unit="DAN-Oxu2hp70USJgW3wn"
              data-ad-width="250"
              data-ad-height="250"
            />
          </div>
        </div>
      </div>

      <ConsentModal
        isOpen={showConsent}
        onClose={handleDisagree}
        onAgree={handleAgree}
      />
      {showFaceRadar && (
        <Suspense fallback={null}>
          <RealtimeFaceRadar
            onCapture={handleRadarCapture}
            onClose={() => setShowFaceRadar(false)}
          />
        </Suspense>
      )}
    </div>
  );
}

export default Info;
